/**
 * Auth hooks. ``useCurrentUser`` is the app's single source of the
 * signed-in user and never retries a 401: that is auth state ("not logged
 * in"), not a transient failure.
 */

"use client"

import { useRouter } from "next/navigation"
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query"

import { ApiError } from "@/lib/api/client"
import {
  isUnauthorized,
  suppressSessionExpiryMeta,
} from "@/lib/auth/session-expiry"

import { fetchCurrentUser, signOut } from "./api"
import type { CurrentUser } from "./types"

export const currentUserKey = ["auth", "me"] as const

const FIVE_MINUTES_MS = 5 * 60 * 1000

export function useCurrentUser(): UseQueryResult<CurrentUser, ApiError> {
  return useQuery<CurrentUser, ApiError>({
    queryKey: currentUserKey,
    queryFn: ({ signal }) => fetchCurrentUser({ signal }),
    staleTime: FIVE_MINUTES_MS,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) {
        return false
      }
      return failureCount < 1
    },
  })
}

/**
 * The signed-in user, or ``null`` once it cannot be loaded at all.
 * ``undefined`` while loading and on a 401, which the sign-in prompt owns; a
 * failed background re-fetch keeps the cached user.
 */
export function loadedCurrentUser(
  query: Pick<UseQueryResult<CurrentUser, ApiError>, "data" | "error">,
): CurrentUser | null | undefined {
  if (query.data) return query.data
  if (query.error === null || isUnauthorized(query.error)) return undefined
  return null
}

export function useSignOut(): UseMutationResult<void, ApiError, void> {
  const router = useRouter()
  const queryClient = useQueryClient()

  return useMutation<void, ApiError, void>({
    mutationFn: () => signOut(),
    // A logout whose POST 401s (session already dead) must not trip the
    // global re-auth prompt; onSettled routes to /signin either way.
    meta: suppressSessionExpiryMeta,
    onSettled: () => {
      queryClient.removeQueries({ queryKey: currentUserKey })
      router.replace("/signin")
    },
  })
}
